import { Box, VStack } from "@chakra-ui/layout"
import React, { useMemo } from "react"
import { Control, Controller } from "react-hook-form"

import { GetAuthRegister } from "~/api/auth/register/get"
import { InputNumber } from "~/components/atoms/InputNumber"
import Selectbox from "~/components/atoms/Selectbox"
import Typography from "~/components/atoms/Typography"
import { DoubleColumnsGrid } from "~/components/layouts/DoubleColumnsGrid"
import { FormContainer } from "~/components/molecules/FormContainer"
import SelectboxMultiple from "~/components/molecules/SelectboxMultiple"
import { Color, SpacingSize } from "~/consts/style-mixins"
import { createOptions } from "~/services/createOptions"

import { Inline } from "../components/Inline"
import { Section } from "../components/Section"
import { LABELS, VALIDATE_MAP } from "../constants"
import { HackedPostAuthRegisterBody } from "../types"

type Props = {
  control: Control<HackedPostAuthRegisterBody>
  formMasterData: GetAuthRegister
}

export const LongInternship: React.VFC<Props> = ({ control, formMasterData }) => {
  const {
    register,
    _formState: { errors },
  } = control

  const options = useMemo(
    () => ({
      majorOccupations: createOptions(formMasterData.major_occupations),
      frequency: createOptions(formMasterData.days_for_work, { isAddInitialOption: true }),
      frequencyWeekday: createOptions(formMasterData.days_for_work_in_weekday, { isAddInitialOption: true }),
      period: createOptions(formMasterData.intern_periods, { isAddInitialOption: true }),
    }),
    [formMasterData]
  )

  return (
    <Section title="長期インターンについて">
      <VStack spacing={SpacingSize._2XL}>
        <FormContainer
          label={LABELS.majorOccupationIdsForLongInternship}
          errorMessage={errors.major_occupation_ids_for_long_internship?.message}
        >
          <Controller
            name="major_occupation_ids_for_long_internship"
            control={control}
            rules={{ shouldUnregister: true }}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <SelectboxMultiple
                label={LABELS.majorOccupationIdsForLongInternship}
                options={options.majorOccupations ?? []}
                selected={value ?? []}
                onChange={onChange}
                error={!!error}
              />
            )}
          />
        </FormContainer>

        <DoubleColumnsGrid>
          <FormContainer
            label={
              <>
                <Inline color={Color.Attention}>1週間</Inline>の出勤可能日数
              </>
            }
            errorMessage={errors.frequency?.message}
          >
            <Selectbox
              options={options.frequency ?? []}
              selectProps={register("frequency", {
                valueAsNumber: true,
                shouldUnregister: true,
              })}
              isError={!!errors.frequency}
            />
          </FormContainer>

          <FormContainer
            label={
              <>
                <Inline color={Color.Attention}>1週間</Inline>
                の出勤可能な
                <Inline color={Color.Attention}>平日</Inline>
                の日数
              </>
            }
            errorMessage={errors.frequency_weekday?.message}
          >
            <Selectbox
              options={options.frequencyWeekday ?? []}
              selectProps={register("frequency_weekday", {
                valueAsNumber: true,
                shouldUnregister: true,
              })}
              isError={!!errors.frequency_weekday}
            />
          </FormContainer>
        </DoubleColumnsGrid>

        <DoubleColumnsGrid>
          <Box>
            <FormContainer
              label={
                <>
                  <Inline color={Color.Attention}>1週間の合計</Inline>
                  出勤可能時間
                </>
              }
              errorMessage={errors.available_hours_per_week?.message}
            >
              <InputNumber
                {...register("available_hours_per_week", {
                  valueAsNumber: true,
                  shouldUnregister: true,
                })}
                placeholder="15"
                unit="時間"
                inputWidth="80px"
                error={!!errors.available_hours_per_week}
              />
            </FormContainer>
            <Box mt={SpacingSize.XS}>
              <Typography type="SubNote">
                企業は<Inline color={Color.Attention}>週15時間以上</Inline>の勤務を求めてます
              </Typography>
            </Box>
          </Box>

          <FormContainer label={LABELS.period} errorMessage={errors.period?.message}>
            <Selectbox
              options={options.period ?? []}
              selectProps={register("period", {
                valueAsNumber: true,
                shouldUnregister: true,
              })}
              isError={!!errors.period}
            />
          </FormContainer>
        </DoubleColumnsGrid>
      </VStack>
    </Section>
  )
}
